export function getPersistableVariables(block: Record<string, unknown>): unknown {
  return block.variables ?? block.state
}

/**
 * Canonical `ui` column payload — the SINGLE builder every persistence path
 * shares (PI-4). The editor subscription (auto-save-bridge) and the F3 flow
 * rename/delete element-rewrite saves (register-studio-tools) write the same
 * LWW dedupe key `block:<id>:ui`, so they must produce the same shape: the
 * DB ui column canonically stores `{ schemaVersion, blockType, root }`
 * (project-api saveBlock/buildBlockRows). A divergent payload (e.g. queueing
 * the nested `block.ui` verbatim, which toLoadedApp builds WITHOUT blockType)
 * silently erased blockType from the column — reload then fell back to
 * block_name. Tolerates both block shapes: nested v2 `{ ui: { schemaVersion,
 * root } }` and the flat v1 alias (`root`/`blockType` top-level) — a
 * flat-root-only block previously queued `data: undefined`, which
 * JSON.stringify dropped from the PATCH body entirely.
 */
export function buildUiFieldPayload(block: Record<string, unknown>): {
  schemaVersion: unknown
  blockType: unknown
  root: unknown
} {
  const ui = block.ui as Record<string, unknown> | undefined
  const meta = block.meta as Record<string, unknown> | undefined
  return {
    schemaVersion: block.schemaVersion ?? ui?.schemaVersion,
    blockType: block.blockType ?? meta?.type ?? meta?.name,
    root: block.root ?? ui?.root,
  }
}

export function getPersistableDatasource(block: Record<string, unknown>): unknown {
  return block.datasource ?? block.data
}

export function shouldSkipPersistSnapshot(input: {
  projectChanged: boolean
  isDirty: boolean
  saveError: unknown
  snapshot: string
  lastSnapshot: string
}): boolean {
  if (!input.isDirty && !input.saveError) return true
  return !input.projectChanged && input.snapshot === input.lastSnapshot
}

export function shouldSkipEditorPersistSnapshot(input: {
  projectChanged: boolean
  snapshot: string
  lastSnapshot: string
}): boolean {
  return input.projectChanged || input.snapshot === input.lastSnapshot
}
